import { SpokenVoice } from '@euler/components/voice-input/functions/useVoiceTurn';

/** How a spoken trainee line sounded, kept on the device for this scene. */
export interface TraineeVoice {
  /**
   * The voice's levels (0 to 1), early to late, as many as its waveform
   * draws (`waveformBars`).
   */
  levels: readonly number[];
  durationMs: number;
  /** The hold as a 16 kHz mono WAV, base64; null when none came. */
  audio: string | null;
  /**
   * The server's token for this voice, parked until the line is sent: sent
   * with the line, so the voice is stored with it; null when none came.
   */
  token: string | null;
}

/** A line's waveform: a bar for about this long, within the bounds below. */
const kMsPerBar = 120;
const kMinBars = 12;
const kMaxBars = 36;

/**
 * Bars a spoken line's waveform has, the customer's and the trainee's
 * alike: a longer line draws a longer wave.
 */
export function waveformBars(durationMs: number): number {
  return Math.min(
    kMaxBars,
    Math.max(kMinBars, Math.round(durationMs / kMsPerBar)),
  );
}

/**
 * A hold's levels (about ten a second) as its waveform's bars, each its
 * loudest, against the loudest of the hold, as the customer's waveforms
 * are drawn.
 */
export function compressLevels(
  levels: readonly number[],
  durationMs: number,
): number[] {
  const count = waveformBars(durationMs);
  if (!levels.length) return Array(count).fill(0);
  const bars = Array.from({ length: count }, (_, bar) => {
    const from = Math.floor((bar * levels.length) / count);
    const to = Math.max(
      from + 1,
      Math.floor(((bar + 1) * levels.length) / count),
    );
    return Math.max(...levels.slice(from, Math.min(to, levels.length)));
  });
  const loudest = Math.max(...bars);
  return loudest > 0 ? bars.map(x => x / loudest) : bars;
}

/** A spoken hold as the scene keeps it: its levels as the waveform's bars. */
export function traineeVoiceOf(spoken: SpokenVoice): TraineeVoice {
  return {
    levels: compressLevels(spoken.levels, spoken.durationMs),
    durationMs: spoken.durationMs,
    audio: spoken.audio,
    token: spoken.token,
  };
}
